import { registerPlugin } from '@capacitor/core'
import { getNativePlatform } from '../utils/nativeRuntime'
import { parseNativeIntentEnvelope, type NativeIntentEnvelope } from './nativeIntentProtocol'
import { queueWorkspaceNativeMutation } from './workspaceMutationQueue'

export interface NativeIntentContractInfo {
  schemaVersion: number
  defaultTtlMs: number
  terminalRetentionMs: number
  maxAttempts: number
  offerBatchSize: number
  maxPayloadBytes: number
  workspaceBinding: string
  installWorkspaceBinding: string
  kinds: string[]
  states: string[]
}

export interface NativeIntentInboxPlugin {
  addListener(
    eventName: 'intentAvailable' | 'quickAccessRefreshRequested',
    listener: () => void,
  ): Promise<{ remove: () => Promise<void> }>
  setStudioAppearance?(options: { palette: string; appearance: 'midnight' | 'daylight' }): Promise<void>
  getContract(): Promise<NativeIntentContractInfo>
  bindWorkspace(options: { workspaceBinding: string }): Promise<void>
  useInstallWorkspace(): Promise<void>
  getAccountOfflineSummary?(options: { workspaceBinding: string }): Promise<{ pending: number }>
  removeAccountOfflineContent?(options: { workspaceBinding: string; confirmed: true }): Promise<void>
  offerPending(): Promise<{ envelopes: unknown[] }>
  acknowledge(options: { id: string; revision: number }): Promise<{ envelope: unknown }>
  complete(options: { id: string; revision: number }): Promise<{ envelope: unknown }>
  setActiveEditor(options: { noteId: string | null }): Promise<void>
  getShareFile(options: { id: string; token: string }): Promise<{ uri: string; size: number }>
  requestPinNoteShortcut(options: { noteId: string }): Promise<{
    supported: boolean
    requested: boolean
  }>
  setQuickAccessNotes?(options: {
    notes: Array<{ id: string; title: string }>
    completeRefresh?: boolean
    refreshMinimumIndicatorMs?: number
    refreshFeedbackDurationMs?: number
  }): Promise<void>
  setQuickAccessSyncCredentials?(options: {
    userId: string
    projectionUrl: string
    widgetCredential: string
    expiresAtMs: number
    isDev: boolean
    backgroundRefreshIntervalMinutes: number
    networkTimeoutMs: number
    maxNetworkAttempts: number
    retryDelayMs: number
    minimumIndicatorMs: number
    successIndicatorMs: number
    failureIndicatorMs: number
    reconnectMinRejectedAttempts: number
    reconnectGraceMs: number
    backgroundRepairBackoffMs: number
    backgroundRepairMaxAttempts: number
  }): Promise<void>
  clearQuickAccessSyncCredentials?(options?: { clearNotes?: boolean }): Promise<void>
  getQuickAccessSyncCredentialStatus?(options: {
    userId: string
    renewalWindowMs: number
  }): Promise<{
    usable: boolean
    credentialMode: string
    expiresAtMs: number
    credentialRejections: number
  }>
  getQuickAccessSyncDiagnostics?(): Promise<{
    attemptId: number
    attemptedAtMs: number
    trigger: 'manual' | 'periodic' | 'none'
    outcome: 'running' | 'succeeded' | 'failed' | 'none'
    stage: string
    code: string
    elapsedMs: number
    network: string
    requestAttempts: number
    consecutiveFailures: number
    firstFailureAtMs: number
    credentialRejections: number
    firstCredentialRejectionAtMs: number
    lastCredentialRefreshAtMs: number
    credentialMode: string
    credentialExpiresAtMs: number
    recentAttempts: Array<{
      attemptId: number
      attemptedAtMs: number
      trigger: 'manual' | 'periodic'
      outcome: 'succeeded' | 'failed'
      stage: string
      code: string
      elapsedMs: number
      network: string
      requestAttempts: number
    }>
  }>
}

export const NativeIntentInbox = registerPlugin<NativeIntentInboxPlugin>('MiniManyIntentInbox')

export function getNativeIntentInbox(
  container: unknown = globalThis,
  plugin: NativeIntentInboxPlugin = NativeIntentInbox,
): NativeIntentInboxPlugin | null {
  if (getNativePlatform(container) !== 'android') return null
  return new Proxy(plugin, { get(target, key, receiver) {
    const method = Reflect.get(target, key, receiver)
    if (!['bindWorkspace', 'useInstallWorkspace', 'setActiveEditor', 'setQuickAccessNotes', 'setQuickAccessSyncCredentials', 'requestPinNoteShortcut', 'acknowledge', 'complete'].includes(String(key)) || typeof method !== 'function') return method
    return (...args: unknown[]) => queueWorkspaceNativeMutation(async () => Reflect.apply(method, target, args))
  } })
}

export async function clearNativeAccountWorkspace(): Promise<void> {
  if (getNativePlatform(window) !== 'android') return
  await queueWorkspaceNativeMutation(async () => {
    await NativeIntentInbox.clearQuickAccessSyncCredentials?.({ clearNotes: true })
    await NativeIntentInbox.setActiveEditor({ noteId: null })
    await NativeIntentInbox.useInstallWorkspace()
  }, true)
}

export async function offerValidatedNativeIntents(
  plugin: NativeIntentInboxPlugin,
): Promise<NativeIntentEnvelope[]> {
  const result = await plugin.offerPending()
  if (!Array.isArray(result.envelopes)) {
    throw new Error('Native intent inbox returned an invalid offer.')
  }
  return result.envelopes.map(parseNativeIntentEnvelope)
}
